import {
  type HandlersResponseData,
  HandlersResCode,
} from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { showErrorData, showSuccessData } from '@/lib/toast'

/**
 * 业务码消息映射
 * 每个业务码对应一个用户友好的提示消息
 */
const CODE_MESSAGES: Record<HandlersResCode, string> = {
  // 成功
  [HandlersResCode.CodeSuccess]: '操作成功',

  // 通用错误 1001-1099
  [HandlersResCode.CodeInvalidParam]: '参数无效',

  // 认证相关 1100-1199
  [HandlersResCode.CodeNeedLogin]: '需要登录',
  [HandlersResCode.CodeInvalidToken]: '无效的令牌',
  [HandlersResCode.CodeTokenExpired]: '令牌已过期',

  // 用户相关 1200-1299
  [HandlersResCode.CodeUserNotFound]: '用户不存在',
  [HandlersResCode.CodeUsernameTaken]: '用户名已被使用',
  [HandlersResCode.CodeEmailTaken]: '邮箱已被使用',
  [HandlersResCode.CodeAccountNotActive]: '账号未激活',
  [HandlersResCode.CodeInvalidCredentials]: '账号或密码错误',
  [HandlersResCode.CodeVerificationFailed]: '验证失败',
  [HandlersResCode.CodePhoneTaken]: '手机号已被使用',
  [HandlersResCode.CodePhoneNotRegistered]: '手机号未注册',
  [HandlersResCode.CodePhoneBound]: '手机号已绑定',
  [HandlersResCode.CodePhoneNotBound]: '手机号未绑定',

  // 服务器错误 1300-1399
  [HandlersResCode.CodeServerBusy]: '服务器繁忙',
  [HandlersResCode.CodeDatabaseError]: '数据库错误',
  [HandlersResCode.CodeEmailSendFailed]: '邮件发送失败',
  [HandlersResCode.CodeTokenGenerateFailed]: '令牌生成失败',

  // 会话相关 1400-1499
  [HandlersResCode.CodeSessionNotFound]: '会话不存在',
  [HandlersResCode.CodePermissionDenied]: '权限不足',
  [HandlersResCode.CodeInvalidSessionID]: '无效的会话ID',
  [HandlersResCode.CodeHistoryFailed]: '历史记录获取失败',
  [HandlersResCode.CodeInsufficientBalance]: '余额不足',

  // AI 相关 1500-1599
  [HandlersResCode.CodeAIRateLimit]: 'AI 限流',
  [HandlersResCode.CodeAIServerBusy]: 'AI 服务繁忙',
  [HandlersResCode.CodeAITimeout]: 'AI 调用超时',
  [HandlersResCode.CodeAIParseFailed]: '解析失败',
  [HandlersResCode.CodeAISchemaInvalid]: 'Schema 验证失败',
  [HandlersResCode.CodeAIContextTooLong]: '上下文过长',
  [HandlersResCode.CodeAIProviderDown]: '厂商不可用',
  [HandlersResCode.CodeAIGenerateFailed]: 'AI 生成失败',

  // 短信相关 1600-1699
  [HandlersResCode.CodeSmsSendFailed]: '短信发送失败',
  [HandlersResCode.CodeSmsCodeExpired]: '验证码已过期',
  [HandlersResCode.CodeSmsCodeInvalid]: '验证码错误',
  [HandlersResCode.CodeSmsTooFrequent]: '发送太频繁',
  [HandlersResCode.CodeSmsEncryptFail]: '手机号加密失败',

  // 操作相关 1700-1799
  [HandlersResCode.CodeIdempotencyFailed]: '幂等性检查失败',
  [HandlersResCode.CodeOperationFailed]: '操作失败',
  [HandlersResCode.CodeCheckinFailed]: '签到失败',
  [HandlersResCode.CodeTaskSubmitFailed]: '任务提交失败',
  [HandlersResCode.CodeDeleteFailed]: '删除失败',
}

/**
 * 通用业务码处理函数
 * 根据返回的业务码自动 logger 和 show 对应的消息
 *
 * @param data - 响应数据
 * @param options - 配置选项
 * @param options.onSuccess - 成功回调函数
 * @param options.successMessage - 自定义成功消息（覆盖默认消息）
 * @param options.errorMessage - 自定义错误消息前缀
 * @param options.customMessages - 自定义某些业务码的消息（覆盖默认消息）
 *
 * @example
 * ```ts
 * handleCodeMsg(data, {
 *   successMessage: '登录成功',
 *   onSuccess: (data) => {
 *     // 处理成功逻辑
 *   },
 *   customMessages: {
 *     [HandlersResCode.CodePhoneTaken]: '该手机号已被占用，请使用其他手机号',
 *   }
 * })
 * ```
 */
export const handleCodeMsg = <T extends HandlersResponseData>(
  data: T,
  options: {
    onSuccess?: (data: T) => void
    successMessage?: string
    errorMessage?: string
    customMessages?: Partial<Record<HandlersResCode, string>>
  } = {}
) => {
  const code = data.code ?? HandlersResCode.CodeOperationFailed

  // 合并自定义消息
  const messages = { ...CODE_MESSAGES, ...options.customMessages }

  switch (code) {
    case HandlersResCode.CodeSuccess: {
      const message = options.successMessage || messages[code]
      logger.info(message, data)
      if (options.successMessage) {
        showSuccessData(message)
      }
      options.onSuccess?.(data)
      break
    }

    case HandlersResCode.CodeInvalidParam:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    // 认证相关 1100-1199
    case HandlersResCode.CodeNeedLogin:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeInvalidToken:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeTokenExpired:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    // 用户相关 1200-1299
    case HandlersResCode.CodeUserNotFound:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeUsernameTaken:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeEmailTaken:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAccountNotActive:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeInvalidCredentials:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeVerificationFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodePhoneTaken:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodePhoneNotRegistered:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodePhoneBound:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    // 服务器错误 1300-1399
    case HandlersResCode.CodeServerBusy: {
      let msg = messages[code]
      if (data.msg) {
        msg = data.msg
      }
      logger.error(messages[code], data)
      showErrorData(msg)
      break
    }

    case HandlersResCode.CodeDatabaseError:
      logger.error(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeEmailSendFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeTokenGenerateFailed:
      logger.error(messages[code], data)
      showErrorData(messages[code])
      break

    // 会话相关 1400-1499
    case HandlersResCode.CodeSessionNotFound:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodePermissionDenied:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeInvalidSessionID:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeHistoryFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeInsufficientBalance:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    // AI 相关 1500-1599
    case HandlersResCode.CodeAIRateLimit:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAIServerBusy:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAITimeout:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAIParseFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAISchemaInvalid:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAIContextTooLong:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAIProviderDown:
      logger.error(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeAIGenerateFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    // 短信相关 1600-1699
    case HandlersResCode.CodeSmsSendFailed: {
      let message = messages[code]
      if (data.msg) {
        message = data.msg
      }
      logger.warn(message, data)
      showErrorData(message)
      break
    }

    case HandlersResCode.CodeSmsCodeExpired:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeSmsCodeInvalid:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeSmsTooFrequent:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeSmsEncryptFail:
      logger.error(messages[code], data)
      showErrorData(messages[code])
      break

    // 操作相关 1700-1799
    case HandlersResCode.CodeIdempotencyFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeOperationFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeCheckinFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeTaskSubmitFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    case HandlersResCode.CodeDeleteFailed:
      logger.warn(messages[code], data)
      showErrorData(messages[code])
      break

    default:
      logger.warn(`${options.errorMessage || '未知错误'}:`, data)
      showErrorData(data.msg || options.errorMessage || '操作失败', data)
      break
  }
}
